import * as React from "react";
import {
	Autocomplete, Backdrop, Box, Button, Card, CardContent, CardHeader, CircularProgress,
	Container, createFilterOptions, Dialog, DialogActions, DialogContent, DialogContentText,
	DialogTitle, IconButton, Paper, Slide, Stack, TextField, Typography, useTheme, Alert, Snackbar, useMediaQuery
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteIcon from "@mui/icons-material/Delete";
import LayersIcon from "@mui/icons-material/Layers";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {useNavigate, useLocation} from "react-router-dom";
import {
	useGetCategoriesQuery,
	useAddCategoryMutation,
	useDeleteCatalogEntityMutation,
} from "../../store/catalogApi";
import {categoryList} from "../DepartmentList";
import {ThemeProvider} from "@mui/material/styles";
import {buttonTheme} from "../../theme";
/* ───────────────── helpers / constants ───────────────── */
const filter = createFilterOptions();
const green = {
	main: "#059669",
	light: "#d1fae5",
	dark: "#047857",
};

const emeraldBg = "linear-gradient(90deg,#0f172a 0%,#1e293b 100%)";

const Transition = React.forwardRef(function Transition(props, ref) {
	return <Slide direction="up" ref={ref} {...props} />;
});

/* ═══════════════════════════════════════════════════════ */
export default function ManageCategory() {
	/* ---------- hooks / state ---------- */
	const theme = useTheme();
	const navigate = useNavigate();
	const location = useLocation();
	const isXs = useMediaQuery(theme.breakpoints.down("sm"));
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const deptData = location.state?.departmentName ? location.state : null;
	const {data: posts = [], isLoading, isFetching, isError} = useGetCategoriesQuery(
		{vendorId: deptData?.vendorId, parentId: deptData?.id},
		{skip: !deptData}
	);
	const [addCategory, {isLoading: adding}] = useAddCategoryMutation();
	const [deleteEntity] = useDeleteCatalogEntityMutation();
	const loading = isLoading || isFetching || adding;
	const [value, setValue] = React.useState(null);
	const [dialogOpen, setDialogOpen] = React.useState(false);

	const [confirmOpen, setConfirmOpen] = React.useState(false);
	const [catToDelete, setCatToDelete] = React.useState(null);
	const [deleteLoading, setDeleteLoading] = React.useState(false);

	// snackbar
	const [snack, setSnack] = React.useState({
		open: false,
		msg: "",
		severity: "success",
	});
	const handleSnackClose = () => setSnack((s) => ({...s, open: false}));

	React.useEffect(() => {
		if (isError) {
			setSnack({open: true, msg: "Could not load categories.", severity: "error"});
		}
	}, [isError]);

	const handleDeleteClick = (cat) => {
		setCatToDelete(cat);
		setConfirmOpen(true);
	};

	const confirmDelete = async () => {
		if (!catToDelete) return;
		setDeleteLoading(true);

		try {
			const data = await deleteEntity({
				entityType: "category",
				vendorId: catToDelete.vendorId,
				id: catToDelete.id,
			}).unwrap();

			if (data.canDelete) {
				setSnack({open: true, msg: "Category deleted.", severity: "success"});
			} else {
				setSnack({
					open: true,
					msg: "Cannot delete. There are sub‑categories or products under this category.",
					severity: "error",
				});
			}
		} catch (err) {
			setSnack({
				open: true,
				msg: "Something went wrong. Please try again.",
				severity: "error",
			});
		}

		setDeleteLoading(false);
		setConfirmOpen(false);
		setCatToDelete(null);
	};

	/* ---------- a department must be passed in via route state ---------- */
	React.useEffect(() => {
		if (!deptData) navigate("/md");
	}, [deptData, navigate]);

	React.useEffect(() => {
		localStorage.setItem('visitedSc', 'true');
		// stale caches from the old reducer-based flow
		localStorage.removeItem("CategoryData");
	}, []);

	/* ---------- handlers ---------- */
	const handleAdd = async () => {
		if (!value || !value.category) return;
		const exists = posts.some(
			(p) => p.categoryName.toLowerCase() === value.category.toLowerCase()
		);
		if (exists) return alert("Category already added");

		try {
			await addCategory({
				categoryName: value.category,
				deptId: deptData.id,
				vendorId: deptData.vendorId,
			}).unwrap();
			setValue(null);
			setDialogOpen(false);
		} catch (err) {
			setSnack({open: true, msg: "Could not add category. Please try again.", severity: "error"});
		}
	};

	/* ---------- early loader when deptData unknown ---------- */
	if (!deptData) {
		return (
			<Backdrop
				sx={{color: "#fff", zIndex: theme.zIndex.drawer + 1}}
				open={loading}
			>
				<CircularProgress color="inherit"/>
			</Backdrop>
		);
	}

	return (
		<Box sx={{bgcolor: "#f8fafc", minHeight: "100vh",py: {xs: 2, md: 4}}}>
			<ThemeProvider theme={theme}>
				<Container maxWidth="md">
					{/* ───── header ───── */}
					<Box
						sx={{
							mb: 4,
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
						}}
					>
						<Button
							startIcon={<ArrowBackIosNewIcon/>}
							variant="outlined" color="secondary"
							sx={{
								borderRadius: 2,
								textTransform: "none",
								fontWeight: 600,
								bgcolor: "#fff",
							}}
							onClick={() =>
								navigate(location.state?.fromManageProducts ? "/mp" : "/md")
							}
						>
							Back
						</Button>

						{isMobile ? (
							<Typography
								variant="subtitle2"
								sx={{ fontWeight: 700, fontSize: "1rem" }}
							>
								Categories
							</Typography>
						) : (
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
								<LayersIcon fontSize="small" color="action" />
								<Typography variant="subtitle2" color="text.secondary">
									{deptData.departmentName}
								</Typography>
								<ArrowForwardIosIcon fontSize="inherit" />
								<Typography variant="subtitle2" fontWeight={600}>
									Categories
								</Typography>
							</Box>
						)}

						<Button
							variant="contained"
							color="primary"
							sx={{
								color: '#000',
								borderRadius: 2,
								textTransform: "none",
								fontWeight: 600,
								minWidth: 72,
							}}
							onClick={() =>
								navigate(location.state?.fromManageProducts ? "/mp" : "/home")
							}
						>
							Done
						</Button>
					</Box>

					{/* ───── department context card ───── */}
					<Card
						sx={{
							mt: isXs ? 2 : 3, mb: isXs ? 2 : 3, width: '100%',
							backgroundImage: emeraldBg,  borderRadius: 3,
							color: "#fff", border: "none",
							boxShadow: "0 12px 24px rgba(0,0,0,0.25)",
						}}
					>
						<CardContent
							sx={{
								p: {xs: 3, md: 4}, display: "flex", flexDirection: {xs: "column", sm: "row"},
								alignItems: {sm: "center"}, justifyContent: "space-between", gap: 3
							}}
						>
							<Box>
									<Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1}}>
										<FolderOpenIcon fontSize="small" sx={{color: "rgba(255,255,255,0.7)"}}/>
										<Typography variant="body2" sx={{opacity: 0.8}}>
											Department
										</Typography>
									</Box>
									<Typography variant="h5" fontWeight={700}>
										{deptData.departmentName}
									</Typography>
									<Typography variant="body2" sx={{mt: 0.5, opacity: 0.8}}>
										Manage categories within this department
									</Typography>
								</Box>

								<Button
									startIcon={<AddIcon/>}
									variant="contained"
									fullWidth={isXs}
									color="primary"
									sx={{
										color: "#000",
										borderRadius: 2,
										textTransform: "none",
										fontWeight: 600,
									}}
									onClick={() => setDialogOpen(true)}
								>
									Add Category
								</Button>
						</CardContent>
					</Card>

					{/* ───── categories list / empty state ───── */}
					{posts.length === 0 ? (
						<Card sx={{borderRadius: 3}}>
							<CardContent sx={{textAlign: "center", py: 10}}>
								<FolderOpenIcon
									sx={{
										fontSize: 64,
										color: "text.disabled",
										mb: 2,
										opacity: 0.6,
									}}
								/>
								<Typography variant="h6" mb={1}>
									No categories yet
								</Typography>
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{mb: 3, maxWidth: 380, mx: "auto"}}
								>
									Start organising your {deptData.departmentName.toLowerCase()} products by creating categories.
								</Typography>
								<Button
									startIcon={<AddIcon/>}
									variant="contained"
									color="primary"
									sx={{
										borderRadius: 2,
										textTransform: "none",
										fontWeight: 600,
									}}
									onClick={() => setDialogOpen(true)}
								>
									Create First Category
								</Button>
							</CardContent>
						</Card>
					) : (
						<Card sx={{borderRadius: 3}}>
							<CardHeader
								title={
									<Typography variant="subtitle1" fontWeight={700}>
										Categories
									</Typography>
								}
								subheader={
									<Typography variant="body2" color="text.secondary">
										{posts.length} {posts.length === 1 ? "category" : "categories"} in{" "}
										{deptData.departmentName}
									</Typography>
								}
								action={
									<Box
										sx={{
											bgcolor: "#f1f5f9",
											color: "#334155",
											px: 1.5,
											py: 0.5,
											borderRadius: 2,
											fontSize: 14,
											fontWeight: 600,
										}}
									>
										{posts.length}
									</Box>
								}
								sx={{pb: 0}}
							/>
							<CardContent sx={{pt: 0}}>
								<Stack spacing={2}>
									{posts.map((cat, idx) => (
										<Paper
											key={cat.id ?? cat.categoryName}
											variant="outlined"
											sx={{
												p: 2,
												borderRadius: 2,
												display: "flex",
												alignItems: "center",
												justifyContent: "space-between",
												borderColor: "#e5e7eb",
												transition: "all 0.15s",
												"&:hover": {borderColor: "#d1d5db", bgcolor: "#f9fafb"},
												'&:hover .row-actions': {opacity: 1}
											}}
										>
											{/* left section */}
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													flex: 1,
													cursor: "pointer",
												}}
												onClick={() =>
													navigate("/ssc", {
														state: {
															id: cat.id,
															vendorId: cat.vendorId,
															categoryName: cat.categoryName,
															departmentName: deptData.departmentName,
															deptId: deptData.id,
														},
													})
												}
											>
												<Box
													sx={{
														width: 40,
														height: 40,
														mr: 2,
														borderRadius: 2,
														bgcolor: green.light,
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
													}}
												>
													<FolderOpenIcon sx={{color: green.main}}/>
												</Box>
												<Box>
													<Typography fontWeight={600}>{cat.categoryName}</Typography>
													<Typography
														variant="body2"
														color="text.secondary"
														sx={{fontSize: "0.8125rem"}}
													>
														Click to manage sub-categories
													</Typography>
												</Box>
											</Box>

											{/* hover actions */}
											<Box
												className="row-actions"
												sx={{
													ml: 1,
													display: "flex",
													alignItems: "center",
													opacity: 0,
													transition: "opacity .15s",
												}}
											>
												<IconButton
													size="small"
													onClick={() => handleDeleteClick(cat)}
													sx={{
														color: "#ef4444",
														"&:hover": {bgcolor: "#fee2e2"},
													}}
												>
													<DeleteIcon fontSize="small"/>
												</IconButton>

												<IconButton
													size="small"
													onClick={() =>
														navigate("/ssc", {
															state: {
																id: cat.id,
																vendorId: cat.vendorId,
																categoryName: cat.categoryName,
																departmentName: deptData.departmentName,
																deptId: deptData.id,
															},
														})
													}
													sx={{
														color: "#64748b",
														"&:hover": {bgcolor: "#f1f5f9"},
													}}
												>
													<ChevronRightIcon fontSize="small"/>
												</IconButton>
											</Box>
										</Paper>
									))}
								</Stack>
							</CardContent>
						</Card>
					)}

					{/* centred footer count */}
					{posts.length > 0 && (
						<Box sx={{textAlign: "center", mt: 2}}>
							<Typography variant="caption" color="text.secondary">
								{posts.length} {posts.length === 1 ? "category" : "categories"} configured
							</Typography>
						</Box>
					)}
				</Container>

				<Dialog
					open={confirmOpen}
					onClose={() => setConfirmOpen(false)}
					TransitionComponent={Transition}
					fullWidth
					maxWidth="xs"
					PaperProps={{sx: {borderRadius: 3}}}
				>
					<DialogTitle fontWeight={700}>Delete Category?</DialogTitle>

					<DialogContent dividers sx={{pt: 1.5}}>
						<DialogContentText>
							Deleting <strong>{catToDelete?.categoryName}</strong> will remove the
							category permanently if it has no child items. This action cannot be
							undone. Continue?
						</DialogContentText>
					</DialogContent>

					<DialogActions sx={{px: 3, py: 2}}>
						<Button onClick={() => setConfirmOpen(false)} color="secondary">Cancel</Button>
						<Button
							variant="contained"
							onClick={confirmDelete}
							disabled={deleteLoading}
							onClick={confirmDelete}
							disabled={deleteLoading}
							color="error"
							sx={{
								textTransform: "none",
								fontWeight: 600,
							}}
						>
							{deleteLoading ? <CircularProgress size={20}/> : "Delete"}
						</Button>
					</DialogActions>
				</Dialog>

				{/* ───── Add-Category dialog ───── */}
				<Dialog
					open={dialogOpen}
					onClose={() => setDialogOpen(false)}
					TransitionComponent={Transition}
					fullWidth
					maxWidth="sm"
					PaperProps={{sx: {borderRadius: 3}}}
				>
					<DialogTitle fontWeight={700}>Add New Category</DialogTitle>
					<DialogContent dividers sx={{pt: 1.5}}>
						<DialogContentText mb={3}>
							Enter the name of the category. Select an option from the list or add a new one
							under <strong>{deptData.departmentName}</strong>.
						</DialogContentText>

						<Autocomplete
							value={value}
							onChange={(e, newValue) => {
								if (typeof newValue === "string") {
									setValue({category: newValue});
								} else if (newValue && newValue.inputValue) {
									setValue({category: newValue.inputValue});
								} else {
									setValue(newValue);
								}
							}}
							filterOptions={(options, params) => {
								const filtered = filter(options, params);
								const {inputValue} = params;
								const isExisting = options.some(
									(o) => o.category.toLowerCase() === inputValue.toLowerCase()
								);
								if (inputValue !== "" && !isExisting) {
									filtered.push({
										inputValue,
										category: `Add "${inputValue}"`,
									});
								}
								return filtered;
							}}
							options={categoryList}
							getOptionLabel={(option) =>
								typeof option === "string"
									? option
									: option.inputValue
										? option.inputValue
										: option.category
							}
							selectOnFocus
							clearOnBlur
							handleHomeEndKeys
							freeSolo
							renderOption={(props, option) => (
								<Box component="li" {...props}>
									{option.category}
								</Box>
							)}
							renderInput={(params) => (
								<TextField {...params} label="Category Name" fullWidth/>
							)}
							onKeyDown={(e) => e.key === "Enter" && handleAdd()}
						/>
					</DialogContent>
					<DialogActions sx={{px: 3, py: 2}}>
						<Button color='secondary' onClick={() => setDialogOpen(false)}>Cancel</Button>
						<Button
							variant="contained"
							onClick={handleAdd}
							onClick={handleAdd}
							disabled={!value || !value.category}
							color="primary"
							sx={{
								borderRadius: 2,
								textTransform: "none",
								fontWeight: 600,
							}}
						>
							Add Category
						</Button>
					</DialogActions>
				</Dialog>

				<Snackbar
					open={snack.open}
					autoHideDuration={4000}
					onClose={handleSnackClose}
					anchorOrigin={{vertical: "bottom", horizontal: "center"}}
				>
					<Alert
						onClose={handleSnackClose}
						severity={snack.severity}
						sx={{width: "100%"}}
					>
						{snack.msg}
					</Alert>
				</Snackbar>

				{/* loader */}
				<Backdrop sx={{color: "#fff", zIndex: theme.zIndex.drawer + 1}} open={loading}>
					<CircularProgress color="inherit"/>
				</Backdrop>
			</ThemeProvider>
		</Box>
	);
}
